import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  Button,
} from '@scio3/ui';
import { useBillingDialogStore, openUpgradeWizard } from '../stores/billing-dialog-store';
import { getPlanDisplayName } from '../utils/plan-helpers';

export function UpgradeRequiredDialog() {
  const { t } = useTranslation();
  const activeDialog = useBillingDialogStore((s) => s.activeDialog);
  const dialogContext = useBillingDialogStore((s) => s.dialogContext);
  const closeDialog = useBillingDialogStore((s) => s.closeDialog);

  const minPlanRequired = (dialogContext.minPlanRequired as string) ?? '';
  const title = (dialogContext.title as string) ?? t('Upgrade Required');
  const subtitle =
    (dialogContext.subtitle as string) ??
    t('This feature requires the {{plan}} plan or higher', {
      plan: getPlanDisplayName(minPlanRequired),
    });

  const handleUpgrade = useCallback(() => {
    openUpgradeWizard(minPlanRequired);
  }, [minPlanRequired]);

  const isOpen = activeDialog === 'upgradeRequired';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent
        className="max-h-[85vh] flex flex-col overflow-hidden sm:max-w-sm"
        data-testid="upgrade-required-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" className="cursor-pointer">
              {t('Cancel')}
            </Button>
          </DialogClose>
          <Button onClick={handleUpgrade} className="cursor-pointer">
            {t('Upgrade')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
